import { ChevronRight } from "lucide-react";
import { Link, matchPath, useLocation } from "react-router-dom";
import { Skeleton } from "@nexus/ui";

import { cn } from "@/lib/utils";
import { normalizePath, pathMatches } from "./sidebarNavigation";
import type { SidebarItem } from "@/types/navigation";

export interface BreadcrumbCrumb {
  label: string;
  href?: string;
}

export interface ResolvedCrumb {
  label: string;
  /** Ancestors only: the section and the current page are never links. */
  href?: string;
  kind: "section" | "ancestor" | "current";
}

export interface PageBreadcrumbProps {
  sectionTitle: string;
  sidebarItems: SidebarItem[];
  /** The page's title: the current crumb when no trail is given. */
  title: string;
  titleLoading?: boolean;
  /**
   * The page's full trail, down to the current page, when the navigation alone
   * cannot tell where it sits (a detail, a drill-down). A leading crumb naming
   * the section is ignored: the section is always added the same way.
   */
  breadcrumbTrail?: readonly BreadcrumbCrumb[];
  className?: string;
}

const pathOf = (href: string) => normalizePath(href.split(/[?#]/)[0] ?? href);

/** The navigation items from the root down to the one the page belongs to. */
function sidebarChain(items: readonly SidebarItem[], pathname: string): SidebarItem[] {
  let best: { chain: SidebarItem[]; score: number } = { chain: [], score: -1 };
  function walk(list: readonly SidebarItem[], parents: SidebarItem[]) {
    for (const item of list) {
      if (item.shortcut) continue;
      const chain = [...parents, item];
      const scores = [
        ...(item.activePaths ?? [])
          .filter((pattern) => matchPath({ path: pattern, end: true }, pathname))
          .map((pattern) => normalizePath(pattern).length),
        ...[item.href, item.basePath]
          .filter((path): path is string => Boolean(path) && path !== "/")
          .filter((path) => pathMatches(pathname, pathOf(path)))
          .map((path) => pathOf(path).length),
      ];
      const score = Math.max(-1, ...scores);
      // Deeper wins a tie: a child sharing its group's address is the page.
      if (
        score >= 0 &&
        (score > best.score || (chain.length > best.chain.length && score === best.score))
      )
        best = { chain, score };
      if (item.children?.length) walk(item.children, chain);
    }
  }
  walk(items, []);
  return best.chain;
}

/**
 * The page's place in the product: its section (context, not a link — a
 * section has no page of its own), the pages above it (links), then the page
 * itself (not a link). A link that would lead back to the page, or repeat the
 * crumb before it, is left out.
 */
export function resolveBreadcrumb(
  { sectionTitle, sidebarItems, title, breadcrumbTrail }: Omit<PageBreadcrumbProps, "className">,
  pathname: string,
): ResolvedCrumb[] {
  const currentPath = normalizePath(pathname);
  let ancestors: BreadcrumbCrumb[];
  let current: string;
  if (breadcrumbTrail?.length) {
    const trail =
      breadcrumbTrail[0]?.label === sectionTitle ? breadcrumbTrail.slice(1) : breadcrumbTrail;
    current = trail[trail.length - 1]?.label ?? title;
    ancestors = trail.slice(0, -1);
  } else {
    const chain = sidebarChain(sidebarItems, currentPath);
    const page = chain[chain.length - 1];
    // A navigation destination is the page itself; anything deeper (a detail
    // without a trail) has that destination as its parent.
    const isPage = page !== undefined && pathOf(page.href) === currentPath;
    ancestors = (isPage ? chain.slice(0, -1) : chain)
      // A group that opens on its first entry leads where that entry does: keep the entry.
      .filter(
        (item, index, list) =>
          !list[index + 1] || pathOf(list[index + 1]!.href) !== pathOf(item.href),
      )
      .map(({ label, href }) => ({ label, href }));
    current = title;
  }
  const links = ancestors.filter(
    (crumb, index): crumb is BreadcrumbCrumb & { href: string } =>
      Boolean(crumb.href) &&
      pathOf(crumb.href!) !== currentPath &&
      crumb.label !== ancestors[index - 1]?.label,
  );
  const crumbs: ResolvedCrumb[] = [
    ...links.map((crumb) => ({ label: crumb.label, href: crumb.href, kind: "ancestor" as const })),
    { label: current, kind: "current" },
  ];
  // A page named after its section (Investigations) needs no second mention.
  return crumbs[0]?.label === sectionTitle
    ? crumbs
    : [{ label: sectionTitle, kind: "section" }, ...crumbs];
}

/**
 * The trail sits on one line and never overflows: every crumb can shrink to an
 * ellipsis (its full label stays in its tooltip), the section first, the
 * current page last. Where the band gets very narrow the section gives way.
 */
export function PageBreadcrumb({ className, titleLoading = false, ...props }: PageBreadcrumbProps) {
  const { pathname } = useLocation();
  const crumbs = resolveBreadcrumb(props, pathname);

  return (
    <nav aria-label="Breadcrumb" className={cn("@container/breadcrumb min-w-0 flex-1", className)}>
      <ol className="m-0 flex min-w-0 list-none items-center gap-1.5 overflow-hidden p-0 text-xs font-normal leading-5">
        {crumbs.map((crumb, index) => (
          <li
            key={`${crumb.kind}-${index}`}
            data-crumb={crumb.kind}
            className={cn(
              // Never narrower than its chevron and a letter or two, so crumbs never overlap.
              "flex items-center gap-1.5",
              // Room for the chevron (12px + 6px gap) and a few letters.
              crumb.kind === "current" ? "min-w-[calc(6ch+18px)]" : "min-w-[calc(3ch+18px)]",
              // The longest labels give way the most; the section gives way first.
              crumb.kind === "section" ? "shrink-[3] @max-[16rem]/breadcrumb:hidden" : "shrink",
            )}
          >
            {index > 0 ? (
              <ChevronRight
                className={cn(
                  "size-3 shrink-0 text-muted/50",
                  index === 1 && crumbs[0]?.kind === "section" && "@max-[16rem]/breadcrumb:hidden",
                )}
                strokeWidth={1.8}
                aria-hidden
              />
            ) : null}
            {crumb.kind === "ancestor" ? (
              <Link
                to={crumb.href!}
                title={crumb.label}
                className="min-w-0 truncate text-muted transition-colors hover:text-primary focus-visible:outline-2 focus-visible:outline-focus"
              >
                {crumb.label}
              </Link>
            ) : (
              <span
                title={titleLoading && crumb.kind === "current" ? undefined : crumb.label}
                aria-busy={titleLoading && crumb.kind === "current" ? true : undefined}
                aria-current={crumb.kind === "current" ? "page" : undefined}
                className={cn(
                  "min-w-0 truncate",
                  crumb.kind === "current" ? "font-medium text-secondary" : "text-muted",
                )}
              >
                {titleLoading && crumb.kind === "current" ? (
                  <>
                    <span className="sr-only">{crumb.label}</span>
                    <Skeleton className="h-3 w-24 max-w-full" />
                  </>
                ) : (
                  crumb.label
                )}
              </span>
            )}
          </li>
        ))}
      </ol>
    </nav>
  );
}
